import { z } from 'zod';
/**
 * Public + admin coupon shapes used by the PWA and CMS. Keeps the
 * client free of Mongo-native field names (snake_case → camelCase)
 * and the couponClaim's denormalised rendering shape in one place.
 */
export const CouponDiscountTypeSchema = z.enum([
    'percentage',
    'fixed_amount',
    'bonus_points',
]);
export const CouponAppliesToSchema = z.enum(['session', 'wallet_topup', 'both']);
export const CouponClaimStatusSchema = z.enum([
    'claimed', 'applied', 'expired', 'void',
]);
/** Shape consumed by the driver-side PWA. `alreadyClaimed` reflects
 *  whether *this driver* has reached their per-user claim cap. */
export const CouponPublicSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string().optional(),
    discountType: CouponDiscountTypeSchema,
    discountValue: z.number().nonnegative(),
    appliesTo: CouponAppliesToSchema,
    minPurchaseCents: z.number().nonnegative().optional(),
    maxDiscountCents: z.number().nonnegative().optional(),
    /** ISO instant: 23:59:59.999 Los Angeles time on the coupon's last day.
     *  Show it as its Los Angeles date (formatLosAngelesDate). */
    validUntil: z.string().optional(),
    isActive: z.boolean(),
    autoApply: z.boolean(),
    alreadyClaimed: z.boolean(),
});
export const CouponClaimPublicSchema = z.object({
    id: z.string(),
    coupon: CouponPublicSchema,
    status: CouponClaimStatusSchema,
    claimedAt: z.string(),
    appliedAt: z.string().optional(),
    discountAppliedCents: z.number().nonnegative().optional(),
});
/** Admin-only projection adds cap + analytics columns. */
export const CouponAdminSchema = CouponPublicSchema.extend({
    totalClaimed: z.number().int().nonnegative(),
    maxTotalClaims: z.number().int().positive().optional(),
    maxClaimsPerUser: z.number().int().positive(),
    /** ISO instant: 00:00 Los Angeles time on the coupon's first day. */
    validFrom: z.string().optional(),
    createdAt: z.string(),
});
/** Payload the admin form sends on create / update. All optional
 *  fields are truly optional at the schema level — the route fills
 *  in tenant_id and created_by server-side. */
export const CouponUpsertBodySchema = z.object({
    code: z.string().min(3).max(32),
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    discountType: CouponDiscountTypeSchema,
    discountValue: z.number().nonnegative(),
    appliesTo: CouponAppliesToSchema.default('both'),
    minPurchaseCents: z.number().nonnegative().optional(),
    maxDiscountCents: z.number().nonnegative().optional(),
    maxClaimsPerUser: z.number().int().positive().default(1),
    maxTotalClaims: z.number().int().positive().optional(),
    /** YYYY-MM-DD: valid from 00:00 Los Angeles time that day. '' clears it. */
    validFrom: z.string().optional(),
    /** YYYY-MM-DD: valid through 23:59:59 Los Angeles time that day
     *  (Warren, 29 Oct 2026). '' clears it. */
    validUntil: z.string().optional(),
    isActive: z.boolean().default(true),
    autoApply: z.boolean().default(true),
});
//# sourceMappingURL=coupons.js.map